import React from 'react';
import { H1, P, CenteredSection, Content, Insights, EpisodeProps, PrevEpisodeProps, PODCAST_URL, ShareRouter, useIsMounted } from '@acueducto/shared';
import { Fade } from 'react-awesome-reveal';
import { EpisodePreview } from '../EpisodePreview/EpisodePreview';
import Logo from '@acueducto/shared/assets/images/layout/logo.svg';
import BackArrowIcon from '../../../public/assets/images/layout/backarrow.svg';
import EpisodeNumber from '../EpisodeNumber';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { PrismicRichText } from '@prismicio/react';
import BroadcastRouter from '../BroadcastRouter';
import {
  IntroLogo,
  THoverable,
  AllEpisodesHoverable,
  EpisodeNumberStyled,
  Center,
  ContentType,
  CenteredDiv,
  RouterSpace,
  NextEp,
  VideoContainer,
  Video,
} from './PodcastEpisode.styles';
import { RichTextField } from '@prismicio/client';

// Importación dinámica del reproductor de YouTube para evitar problemas de SSR
// Se carga solo en el cliente para evitar errores de renderizado en el servidor
const NoSSRPlayer = dynamic(() => import('react-player/youtube'), {
  loading: () => <p>Cargando...</p>,
  ssr: false,
});

interface PodcastEpisodePageProps {
  episode: EpisodeProps;
  prevEpisode: PrevEpisodeProps | null;
}

export const PodcastEpisodePage: React.FC<PodcastEpisodePageProps> = ({
  episode,
  prevEpisode,
}) => {
  const {
    title,
    seo_h1,
    insights,
    business,
    slug,
    spotify,
    apple,
    youtube,
    episodeNumber,
    content,
    youtubeImageUrl,

  } = episode;

  const hasInsights = Array.isArray(insights) && insights.some((block: RichTextField[number]) =>
      ('text' in block && block.text.trim().length > 0) ||
      (block.type === 'image' && Boolean(block.url)) ||
      (block.type === 'embed' && Boolean(block.oembed.html?.trim()))
    );

  // Empty Rich Text blocks are valid, but must not create an empty section.
  const hasTranscript = Array.isArray(content) && content.some((block: RichTextField[number]) =>
      ('text' in block && block.text.trim().length > 0) ||
      (block.type === 'image' && Boolean(block.url)) ||
      (block.type === 'embed' && Boolean(block.oembed.html?.trim()))
    );

  // Convierte la URL de YouTube al formato de embed para poder incrustar el video
  const embedYoutube = youtube && youtube.replace('watch?v=', 'embed/');
  // Probablemente pueda heredar el hasLoaded de la página de episodios
  const domLoaded = useIsMounted();

  return (
    <>
      <CenteredSection
        $customBackground="/assets/images/layout/backOld.svg"
        id="cuandoelriosuena"
      >
        {/* Cabecera del podcast con logo y título */}
        <Fade triggerOnce>
          <IntroLogo>
            <Link href="/">
              <THoverable>cuando el río suena</THoverable>
              <span>
                por <Logo className="inline" />
              </span>
            </Link>
          </IntroLogo>
        </Fade>
        <Fade triggerOnce>
          <div>
            {/* Enlace para volver a todos los episodios */}
            <Link href={'/episodios'}>
              <AllEpisodesHoverable>
                <BackArrowIcon />
                <p>ver todos los episodios</p>
              </AllEpisodesHoverable>
            </Link>
            {/* Número de episodio */}
            <EpisodeNumberStyled>
              <EpisodeNumber episodeNumber={episodeNumber} />
            </EpisodeNumberStyled>
            {/* Título del episodio - maneja SEO con h1 oculto si es necesario */}
            {seo_h1 ? (
              <>
                <h1 className="seo_h1">{seo_h1}</h1>
                <P className="h1">
                  {title ? title.charAt(0).toLowerCase() + title.slice(1) : ''}
                </P>
              </>
            ) : (
              <H1>{title ? title.charAt(0).toLowerCase() + title.slice(1) : ''}</H1>
            )}
            {/* Enlaces a plataformas de podcast */}
            <Center style={{ marginTop: '10px' }}>
              <BroadcastRouter
                trackClicks
                episodeNumber={episodeNumber}
                spotify={spotify}
                apple={apple}
                youtube={youtube}
              >
                Escúchalo en
              </BroadcastRouter>
            </Center>
            {/* Reproductor de YouTube si existe URL y el DOM está cargado */}
            {youtube && domLoaded && (
              <Center>
                <VideoContainer>
                  <Video>
                    <NoSSRPlayer
                      className="react-player"
                      light={youtubeImageUrl}
                      url={embedYoutube}
                      controls={true}
                      width="100%"
                      height="100%"
                    />
                  </Video>
                </VideoContainer>
              </Center>
            )}
          </div>
        </Fade>

        {/* Previsualización del episodio con detalles */}
        <Fade triggerOnce>
          <EpisodePreview hideImageMobile {...episode} longFormat />
        </Fade>

        {/* Sección de Insights - Puntos clave del episodio */}
        {hasInsights && (
          <Fade triggerOnce>
            <Content as={Insights} data-episode-insights>
              <ContentType $insights>Insights</ContentType>
              <p>
                Si solo tienes un minuto, lo más importante que pueden aprender
                operadores, inversionistas y fundadores de {business} es lo
                siguiente:
              </p>
              <PrismicRichText field={insights as RichTextField} />
            </Content>
          </Fade>
        )}

        {/* Sección de transcripción del episodio */}
        {hasTranscript && (
          <Fade>
            <Content data-episode-transcript>
              <ContentType>Transcript</ContentType>
              <PrismicRichText field={content as RichTextField} />
            </Content>
          </Fade>
        )}

        {/* Sección para compartir el episodio */}
        <Fade triggerOnce>
          <CenteredDiv>
            {content && spotify && (
              <>
                <RouterSpace>
                  Si crees que a alguien le seria útil este contenido,
                  compártelo con esa persona.
                </RouterSpace>
                <ShareRouter
                  shareUrl={`${PODCAST_URL}/${slug}`}
                />
              </>
            )}
          </CenteredDiv>
        </Fade>

        {/* Sección para mostrar el siguiente episodio */}
        {prevEpisode && (
          <NextEp>
            <p>Escucha otro episodio</p>
            <EpisodePreview {...prevEpisode} simplest />
          </NextEp>
        )}
      </CenteredSection>
    </>
  );
};
